import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { useOfflineDetection } from '@/hooks/useOfflineDetection';
import { NativeInit } from '@/hooks/useNativeInit';
import { AuthProvider } from '@/contexts/AuthContext';
import { LoginDrawerProvider } from '@/contexts/LoginDrawerContext';
import { OverlayStackProvider } from '@/contexts/OverlayStackContext';
import { SidebarProvider } from '@/contexts/SidebarContext';
import { TooltipProvider } from '@/components/shadcn/tooltip';
import { LoginDrawer } from '@/components/auth/LoginDrawer';
import { GuestOnly, RedirectCoHostFromCustomer, RequireAuth, RequireRole } from '@/components/guards/AuthGuards';
import LoginPage from '@/pages/LoginPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import CheckEmailPage from '@/pages/CheckEmailPage';
import ClientPage from '@/pages/ClientPage';
import AdminPage from '@/pages/AdminPage';
import CommunityPage from '@/pages/CommunityPage';
import CommunityLayout from '@/components/admin/CommunityLayout';
import CashflowManagementPage from '@/pages/community/CashflowManagementPage';
import SuperAdminPage from '@/pages/SuperAdminPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

import ClientExportMatchRecapPage from '@/pages/client/export/ClientExportMatchRecapPage';
import { QrisPaymentPage } from '@/pages/payment/QrisPaymentPage';
import ScrollToTop from '@/components/common/ScrollToTop';
import NotFoundPage from '@/pages/NotFoundPage';

function App() {
  useOfflineDetection();

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ScrollToTop />
          <AuthProvider>
            <LoginDrawerProvider>
            <OverlayStackProvider>
              <SidebarProvider>
                <TooltipProvider delayDuration={100}>
                  <NativeInit />
                  <LoginDrawer />
                  <Toaster
                    position="top-center"
                    containerStyle={{
                      top: 'calc(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) + 12px)',
                    }}
                  />
            <Routes>
              {/* Standalone public route for Playwright share recap rendering */}
              <Route path="/event/:slug/export" element={<ClientExportMatchRecapPage />} />

              {/* Standalone public route for QRIS customer scan display */}
              <Route path="/qris/:invoiceNumber" element={<QrisPaymentPage />} />
              <Route path="/payment/qris/:invoiceNumber" element={<QrisPaymentPage />} />

              <Route element={<GuestOnly />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/check-email" element={<CheckEmailPage />} />
                <Route path="/community-login" element={<Navigate to="/host" replace />} />
                <Route path="/register" element={<Navigate to="/login" replace />} />
                <Route path="/reset-password" element={<Navigate to="/forgot-password" replace />} />
              </Route>

              <Route element={<RequireAuth />}>
                <Route
                  element={<RequireRole allowedRoles={['community']} />}
                >
                  <Route path="/cash" element={<CommunityLayout />}>
                    <Route index element={<CashflowManagementPage />} />
                  </Route>
                  <Route path="/host/*" element={<CommunityPage />} />
                </Route>

                <Route
                  element={<RequireRole allowedRoles={['owner', 'staff']} />}
                >
                  <Route path="/admin" element={<AdminPage />} />
                  <Route path="/admin/*" element={<AdminPage />} />
                  <Route path="/admin/:slug/*" element={<AdminPage />} />
                </Route>

                <Route
                  element={<RequireRole allowedRoles={['customer', 'community']} />}
                >
                  <Route element={<RedirectCoHostFromCustomer />}>
                    <Route path="/*" element={<ClientPage />} />
                  </Route>
                </Route>

                <Route
                  element={<RequireRole allowedRoles={['superadmin']} />}
                >
                  <Route path="/superadmin/*" element={<SuperAdminPage />} />
                </Route>
              </Route>

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
                </TooltipProvider>
              </SidebarProvider>
            </OverlayStackProvider>
            </LoginDrawerProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
